import { useState, useCallback, useRef } from 'react';
import { YearbookPage } from '@/types/yearbook';

const MAX_HISTORY = 50;

function deepClone<T>(obj: T): T {
  return structuredClone(obj);
}

export function useHistory(initialState: YearbookPage[]) {
  const [pages, setPages] = useState<YearbookPage[]>(initialState);
  const undoStack = useRef<YearbookPage[][]>([]);
  const redoStack = useRef<YearbookPage[][]>([]);
  const isDraggingRef = useRef(false);
  const snapshotBeforeDrag = useRef<YearbookPage[] | null>(null);

  const pushToUndo = useCallback((snapshot: YearbookPage[]) => {
    undoStack.current = [...undoStack.current.slice(-MAX_HISTORY + 1), deepClone(snapshot)];
    redoStack.current = [];
  }, []);

  /** Call before a discrete action (add, delete, bg change, text edit, etc.) */
  const commitChange = useCallback(
    (updater: (prev: YearbookPage[]) => YearbookPage[]) => {
      setPages((prev) => {
        pushToUndo(prev);
        return updater(prev);
      });
    },
    [pushToUndo]
  );

  /** Call when drag starts – takes a snapshot but does NOT push to undo yet */
  const beginDrag = useCallback(() => {
    isDraggingRef.current = true;
    setPages((prev) => {
      snapshotBeforeDrag.current = deepClone(prev);
      return prev;
    });
  }, []);

  /** Live update during drag – no history push */
  const dragUpdate = useCallback(
    (updater: (prev: YearbookPage[]) => YearbookPage[]) => {
      setPages(updater);
    },
    []
  );

  /** Call when drag ends – pushes the pre-drag snapshot to undo */
  const endDrag = useCallback(() => {
    if (snapshotBeforeDrag.current) {
      undoStack.current = [
        ...undoStack.current.slice(-MAX_HISTORY + 1),
        snapshotBeforeDrag.current,
      ];
      redoStack.current = [];
      snapshotBeforeDrag.current = null;
    }
    isDraggingRef.current = false;
  }, []);

  const undo = useCallback((onRestore?: (prev: YearbookPage[], next: YearbookPage[]) => void) => {
    if (undoStack.current.length === 0) return;
    const previous = undoStack.current.pop()!;
    setPages((current) => {
      redoStack.current = [...redoStack.current, deepClone(current)];
      onRestore?.(current, previous);
      return previous;
    });
  }, []);

  const redo = useCallback((onRestore?: (prev: YearbookPage[], next: YearbookPage[]) => void) => {
    if (redoStack.current.length === 0) return;
    const next = redoStack.current.pop()!;
    setPages((current) => {
      undoStack.current = [...undoStack.current, deepClone(current)];
      onRestore?.(current, next);
      return next;
    });
  }, []);

  const canUndo = undoStack.current.length > 0;
  const canRedo = redoStack.current.length > 0;

  /** Replace all pages without pushing to undo (used for initial load) */
  const loadPages = useCallback((newPages: YearbookPage[]) => {
    setPages(newPages);
    undoStack.current = [];
    redoStack.current = [];
  }, []);

  /** Merge lazy-loaded pages into current state without clearing undo history */
  const mergePages = useCallback((merger: (prev: YearbookPage[]) => YearbookPage[]) => {
    setPages(merger);
  }, []);

  return {
    pages,
    setPages: commitChange,
    loadPages,
    mergePages,
    beginDrag,
    dragUpdate,
    endDrag,
    undo,
    redo,
    canUndo,
    canRedo,
  };
}